import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * The small capitalised label that names a fact or a region — the "eyebrow".
 *
 * This is the one place the product writes all-caps text, and `docs/brand.md`
 * FD-14 reserves it for exactly this role, so the treatment is worth having in
 * one place rather than in each panel that needs it. It was written out six
 * times across the task drawer and the step inspector before this existed, and
 * the copies had already begun to drift.
 *
 * `as="summary"` exists because a collapsed block's label has to *be* the
 * `<summary>` element for the disclosure to work — it cannot be a `<span>`
 * inside one. It is a closed choice of two rather than a general polymorphic
 * component: those are the two elements this label is ever rendered as.
 *
 * The icon is optional because `PageHeader`'s eyebrow has none and never wants
 * one — it names the kind of page, not a fact with a glyph. That eyebrow used
 * to be its own hand-written copy of these classes at a different size, which
 * put two sizes of the same all-caps label on the board at once and made this
 * docblock's claim to be "the one place" false.
 *
 * `text-2xs`, the chip floor of the type scale (spec 122): this label sat at
 * 11 px, below it, and the task drawer's readability pass (spec 096 FR-015)
 * named it as the one readable text under 12 px left on that surface. Running
 * text starts a step up at `text-xs`; an eyebrow is a label, not a sentence.
 * One size for every eyebrow.
 */
export function EyebrowLabel({
  icon: Icon,
  as = 'span',
  className,
  children,
}: {
  icon?: LucideIcon;
  as?: 'span' | 'summary';
  className?: string;
  children: ReactNode;
}) {
  const Tag = as;
  return (
    <Tag
      className={cn(
        'text-muted-foreground flex items-center gap-1.5 text-2xs font-medium tracking-wide uppercase',
        as === 'summary' && 'cursor-pointer',
        className,
      )}
    >
      {Icon ? (
        <Icon aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
      ) : null}
      {children}
    </Tag>
  );
}
